<template>
  <mqtt-status-bar v-if="statusBar" />

  <v-row>
    <v-col class="d-flex" cols="12" md="9" sm="9">
      <stat-card
        description="The total time the broker has been running."
        icon="mdi-clock"
        title="Uptime"
        units="string"
        :value="broker.uptime"
      />
    </v-col>

    <v-col class="d-flex" cols="12" md="3" sm="3">
      <stat-card
        description="The Mosquitto broker version."
        icon="mdi-tag"
        title="Version"
        units="string"
        :value="broker.version"
      />
    </v-col>

    <!-- row 2 -->

    <v-col class="d-flex" cols="12" md="4" sm="6">
      <stat-card
        :animated="true"
        description="The number of currently connected clients."
        icon="mdi-lan-connect"
        title="Clients Connected"
        :value="broker?.clients?.connected"
      />
    </v-col>

    <v-col class="d-flex" cols="12" md="4" sm="6">
      <stat-card
        :animated="true"
        description="The total number of persistent clients (with clean session disabled) that are registered at the broker but are currently disconnected."
        icon="mdi-lan-disconnect"
        title="Clients Disconnected"
        :value="broker?.clients?.disconnected"
      />
    </v-col>

    <v-col class="d-flex" cols="12" md="4" sm="6">
      <stat-card
        :animated="true"
        description="The total number of active and inactive clients currently connected and registered on the broker."
        icon="mdi-account-group"
        title="Total Clients"
        :value="broker?.clients?.total"
      />
    </v-col>

    <!-- row 3 -->

    <v-col class="d-flex" cols="12" md="3" sm="6">
      <stat-card
        :animated="true"
        description="The total number of messages of any type sent since the broker started."
        icon="mdi-message-arrow-right"
        title="Messages Sent"
        :value="broker?.messages?.sent"
      />
    </v-col>

    <v-col class="d-flex" cols="12" md="3" sm="6">
      <stat-card
        :animated="true"
        description="The total number of messages of any type received since the broker started."
        icon="mdi-message-arrow-left"
        title="Messages Received"
        :value="broker?.messages?.received"
      />
    </v-col>

    <v-col class="d-flex" cols="12" md="3" sm="6">
      <stat-card
        :animated="true"
        description="The total number of bytes sent since the broker started."
        icon="mdi-call-made"
        title="Bytes Sent"
        units="bytes"
        :value="broker?.bytes?.sent"
      />
    </v-col>

    <v-col class="d-flex" cols="12" md="3" sm="6">
      <stat-card
        :animated="true"
        description="The total number of bytes received since the broker started."
        icon="mdi-call-received"
        title="Bytes Read"
        units="bytes"
        :value="broker?.bytes?.received"
      />
    </v-col>

    <!-- row 4 -->

    <v-col class="d-flex" cols="12" md="3" sm="6">
      <stat-card
        :animated="true"
        description="The total number of PUBLISH messages sent since the broker started."
        icon="mdi-message"
        title="PUBLISH Messages Sent"
        :value="broker?.publish?.messages?.sent"
      />
    </v-col>

    <v-col class="d-flex" cols="12" md="3" sm="6">
      <stat-card
        :animated="true"
        description="The total number of PUBLISH messages that have been dropped due to inflight/queuing limits."
        icon="mdi-message-alert"
        title="PUBLISH Messages Dropped"
        :value="broker?.publish?.messages?.dropped"
      />
    </v-col>

    <v-col class="d-flex" cols="12" md="3" sm="6">
      <stat-card
        :animated="true"
        description="The total number of subscriptions active on the broker."
        icon="mdi-format-list-checks"
        title="Subscriptions"
        :value="broker?.subscriptions?.count"
      />
    </v-col>

    <v-col class="d-flex" cols="12" md="3" sm="6">
      <stat-card
        :animated="true"
        description="The total number of retained messages active on the broker."
        icon="mdi-forum"
        title="Retained Messages"
        :value="broker?.retained_messages?.count"
      />
    </v-col>

    <!-- row 5 -->

    <v-col class="d-flex" cols="12" md="6" sm="6">
      <stat-card
        :animated="true"
        description="The number of messages currently held in the message store. This includes retained messages and messages queued for durable clients."
        icon="mdi-forum"
        title="Stored Messages"
        :value="broker?.store?.messages?.count"
      />
    </v-col>

    <v-col class="d-flex" cols="12" md="6" sm="6">
      <stat-card
        :animated="true"
        description="The number of bytes currently held by message payloads in the message store. This includes retained messages and messages queued for durable clients."
        icon="mdi-harddisk"
        title="Stored Message Bytes"
        units="bytes"
        :value="broker?.store?.messages?.bytes"
      />
    </v-col>
  </v-row>
</template>

<script setup>
  import { useMqttStore } from '#xapp-core/mqtt'
  import { computed, onMounted, onUnmounted } from 'vue'

  defineProps({
    statusBar: { default: false, type: Boolean },
  })

  const mqttStore = useMqttStore()
  const broker = computed(() => mqttStore.broker || {})

  onMounted(() => {
    // Avoid wildcards where possible and subscribe to only what is needed
    mqttStore.subscribe('$SYS/broker/#')
  })

  onUnmounted(() => {
    mqttStore.unsubscribe('$SYS/broker/#')
  })
</script>
